import * as React from "react";
import { Box, Card, Chip, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";

import { useDialog } from "@/hooks/use-dialog";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { 
  useDeletePlugin,
  pluginsKeys,
  Plugin,
} from "@/hooks/api/use-plugins";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";
import { CreatePlugin } from "./create";

export function Plugins(): React.JSX.Element {
  const [isCreatePluginDrawerOpen, setIsCreatePluginDrawerOpen] = React.useState(false);
  const [selectedPluginToCreate, setSelectedPluginToCreate] = React.useState<Plugin | null>(null);

	const {
		items: plugins,
		isLoading,
		totalItems,
		refetchData: fetchPlugins,
	} = useCachedTableStateWithUrl({
		queryKey: pluginsKeys.lists(),
		endpoint: '/plugins',
	});

	const deletePluginMutation = useDeletePlugin();

	const {
		data: selectedPluginToDelete,
		open: isDeletePluginModalOpen,
		handleClose: handleDeletePluginModalClose,
		handleOpen: handleDeletePluginModalOpen,
	} = useDialog<Plugin | null>();

	const handleDeletePlugin = async () => {
		if (selectedPluginToDelete?.id) {
			await deletePluginMutation.mutateAsync(selectedPluginToDelete.id);
			handleDeletePluginModalClose();
		}
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "title",
					label: "Title",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "title",
			},
			{
				header: {
					id: "Plugin",
					label: "Plugin",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "plugin",
				getCellValue: (row: any) => row.plugin.title,
			},
			{
				header: {
					id: "enabled",
					label: "Enabled",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "enabled",
				getCellValue: (row: any) => {
					const isEnabled = row.enabled;

					if (!isEnabled) {
						return null;
					}

					return (
						<Chip
							// icon={<CheckCircleIcon color="var(--mui-palette-success-main)" weight="fill" />}
							label={"Enabled"}
							size="small"
							variant="filled"
							color="success"
						/>
					);
				},
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedPluginToCreate(row);
										setIsCreatePluginDrawerOpen(true);
									},
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeletePluginModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Plugins Management</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Configure AI plugins
							</Typography>
						</Box>
						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button startIcon={<PlusIcon />} variant="contained" onClick={() => {
								setSelectedPluginToCreate(null);
								setIsCreatePluginDrawerOpen(true);
							}}>
								Create Plugin
							</Button>
						</Box>
					</Stack>

					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={plugins}
								refetchData={fetchPlugins}
								isLoading={isLoading}
								totalItems={totalItems}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeletePluginModalOpen}
				onClose={handleDeletePluginModalClose}
				onDelete={handleDeletePlugin}
				title="Delete Plugin"
				description="Are you sure you want to delete this plugin?"
        warningText="Any functionality (agents, tools, etc.) that uses this plugin will stop working and will need to be reconfigured."
			/>

      <CreatePlugin
        isCreatePluginDrawerOpen={isCreatePluginDrawerOpen}
        onCreateDrawerClose={() => {
          setIsCreatePluginDrawerOpen(false);
          setTimeout(() => {
            setSelectedPluginToCreate(null);
          }, 300);
        }}
        pluginId={selectedPluginToCreate?.id || null}
      />
		</React.Fragment>
	);
}
